import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * DriverAutopilotCard — Tier 3 pitch surface. The optimal controller only
 * matters if the DRIVER can execute it without a screen. So we turn each
 * hold decision into a short spoken cue via the Web Speech API — same
 * primitive Waze/Google Maps use for turn-by-turn.
 *
 * Cues are grounded: they name the bus id, the seconds, and the reason
 * ("bus X is Y min behind"). A real deployment wires this to a bone-
 * conduction earpiece in the driver cabin; here we render a preview and
 * synthesize on button press so a demo audience can hear the actual voice.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MockPill } from "../common/MockPill";
import { getBaseUrl } from "../../lib/api";
const card = {
    padding: "16px 20px 20px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
};
const kicker = {
    fontSize: 12,
    color: "var(--color-cool-ash)",
    lineHeight: 1.5,
};
const scriptBox = {
    padding: "14px 16px",
    border: "1px solid var(--color-pebble)",
    borderRadius: 4,
    background: "#f6f5f2",
    fontFamily: "var(--font-display)",
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.35,
    color: "var(--color-deep-ink, #000d10)",
    minHeight: 72,
};
const controlsRow = {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
};
const playBtn = {
    padding: "10px 16px",
    background: "#000d10",
    color: "#f6e6df",
    border: "1px solid #000d10",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    cursor: "pointer",
};
const ghostBtn = {
    padding: "10px 16px",
    background: "transparent",
    color: "var(--color-deep-ink, #000d10)",
    border: "1px solid var(--color-pebble)",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    cursor: "pointer",
};
const chipRow = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
    fontSize: 11,
    color: "var(--color-cool-ash)",
};
function buildCue(alert) {
    if (!alert) {
        return "All clear. Maintain schedule on Route AS-4.";
    }
    const secs = alert.suggested_hold_s ?? 40;
    const busTail = alert.suggested_hold_bus?.split("-").slice(-1)[0] ?? "your bus";
    const stopTail = alert.suggested_hold_stop?.split("-").slice(-1)[0] ?? "next stop";
    return `${busTail}, hold ${secs} seconds at stop ${stopTail}. Bus behind you closing the gap.`;
}
function browserSpeak(text) {
    const synth = window.speechSynthesis;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.pitch = 0.9;
    const done = new Promise((resolve) => {
        u.onend = () => resolve();
        u.onerror = () => resolve();
    });
    synth.speak(u);
    return { stop: () => synth.cancel(), done };
}
export function DriverAutopilotCard({ alerts }) {
    const activeAlert = useMemo(() => alerts.find((a) => a.displayState === "active") ?? null, [alerts]);
    const [lastPlayed, setLastPlayed] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [voiceSource, setVoiceSource] = useState(null);
    const [copilotEnabled, setCopilotEnabled] = useState(null);
    const audioRef = useRef(null);
    const browserRef = useRef(null);
    const cue = useMemo(() => buildCue(activeAlert), [activeAlert]);
    const speakable = typeof window !== "undefined" && !!window.speechSynthesis;
    useEffect(() => {
        fetch(`${getBaseUrl()}/api/copilot/status`)
            .then((r) => r.json())
            .then((j) => setCopilotEnabled(!!j.enabled))
            .catch(() => setCopilotEnabled(false));
    }, []);
    const stop = useCallback(() => {
        audioRef.current?.pause();
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current = null;
        }
        browserRef.current?.stop();
        browserRef.current = null;
        window.speechSynthesis?.cancel();
        setPlaying(false);
    }, []);
    const play = useCallback(async () => {
        stop();
        setPlaying(true);
        setLastPlayed(new Date().toLocaleTimeString());
        // Try OpenAI TTS first — falls through to browser speechSynthesis on error.
        if (copilotEnabled) {
            try {
                const resp = await fetch(`${getBaseUrl()}/api/copilot/tts`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ text: cue }),
                });
                if (resp.ok) {
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    const audio = new Audio(url);
                    audioRef.current = audio;
                    audio.onended = () => {
                        setPlaying(false);
                        URL.revokeObjectURL(url);
                    };
                    audio.onerror = () => {
                        setPlaying(false);
                        URL.revokeObjectURL(url);
                    };
                    setVoiceSource("openai");
                    await audio.play();
                    return;
                }
            }
            catch {
                // fall through
            }
        }
        if (speakable) {
            setVoiceSource("browser");
            const handle = browserSpeak(cue);
            browserRef.current = handle;
            handle.done.then(() => setPlaying(false));
            return;
        }
        setPlaying(false);
    }, [copilotEnabled, cue, speakable, stop]);
    return (_jsxs("div", { style: card, "data-testid": "autopilot-card", children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }, children: [_jsx(MockPill, { kind: "simulated", label: "DRIVER AUTOPILOT" }), copilotEnabled ? (_jsx(MockPill, { kind: "live", label: "OPENAI TTS \u00B7 HD" })) : (_jsx(MockPill, { kind: "simulated", label: "BROWSER TTS" })), _jsx(MockPill, { kind: "coming-soon", label: "BONE-CONDUCTION" })] }), _jsx("div", { style: kicker, children: "Optimal control only lands if the driver can execute it without a screen. Each hold decision becomes one short spoken cue \u2014 synthesized below via Web Speech API so you can hear the exact voice a Mumbai BEST driver would." }), _jsxs("div", { style: scriptBox, "aria-live": "polite", children: ["\u201C", cue, "\u201D"] }), _jsxs("div", { style: controlsRow, children: [_jsx("button", { type: "button", style: playBtn, onClick: play, disabled: !speakable || playing, "data-testid": "autopilot-play", children: playing ? "Playing…" : "▶  Play cue" }), _jsx("button", { type: "button", style: ghostBtn, onClick: stop, disabled: !playing, "data-testid": "autopilot-stop", children: "Stop" })] }), _jsxs("div", { style: chipRow, children: [_jsxs("span", { children: ["Voice:", " ", voiceSource === "openai"
                                ? "OpenAI tts-1-hd · onyx"
                                : voiceSource === "browser"
                                    ? "browser speechSynthesis"
                                    : copilotEnabled
                                        ? "OpenAI tts-1-hd (ready)"
                                        : "browser speechSynthesis"] }), _jsx("span", { children: "\u00B7" }), _jsx("span", { children: "Rate 0.95 \u00B7 Pitch 0.9" }), lastPlayed ? (_jsxs(_Fragment, { children: [_jsx("span", { children: "\u00B7" }), _jsxs("span", { children: ["Last cue: ", lastPlayed] })] })) : null, !speakable ? (_jsxs(_Fragment, { children: [_jsx("span", { children: "\u00B7" }), _jsx("span", { style: { color: "#a25c46" }, children: "speechSynthesis unavailable \u2014 audio muted" })] })) : null] }), _jsx("div", { style: {
                    fontSize: 12,
                    color: "var(--color-cool-ash)",
                    lineHeight: 1.5,
                    paddingTop: 4,
                    borderTop: "1px dashed var(--color-pebble)",
                }, children: "Same primitive as turn-by-turn navigation. Production wires this to a driver-cabin bone-conduction earpiece so hearing the cue does not compete with road noise. English shown; deployed in Marathi + Hindi." })] }));
}
